import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import CreateEventButton from '../components/CreateEventButton';
import { 
  DollarSign, 
  Calendar as CalendarIcon, 
  Clock,
  Star,
  MapPin,
  Briefcase,
  CheckCircle2,
  Plus,
  Upload,
  Image as ImageIcon,
  X,
  Camera,
  User,
  MessageSquare,
  FileText,
  Receipt,
  Wallet,
  ArrowDownToLine,
  Eye,
  Check,
  XCircle,
  Loader2,
  Building,
  Users,
  ExternalLink,
  Handshake
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_OPTIONS = [
  { value: 'chef', label: 'Chef' },
  { value: 'bartender', label: 'Bartender' },
  { value: 'server', label: 'Server' },
  { value: 'dj', label: 'DJ' },
  { value: 'photographer', label: 'Photographer' },
  { value: 'coordinator', label: 'Event Coordinator' },
  { value: 'setup', label: 'Setup/Breakdown' }
];

const PLACEHOLDER_IMAGES = {
  chef: 'https://images.unsplash.com/photo-1577219491135-ce391730fb2c?w=400',
  bartender: 'https://images.unsplash.com/photo-1516997121675-4c2d1684aa3e?w=400',
  dj: 'https://images.unsplash.com/photo-1571266028243-3716f02d2d2e?w=400',
  photographer: 'https://images.unsplash.com/photo-1554048612-b6a482bc67e5?w=400',
  server: 'https://images.unsplash.com/photo-1559339352-11d035aa65de?w=400',
  coordinator: 'https://images.unsplash.com/photo-1560439514-4e9645039924?w=400',
  setup: 'https://images.unsplash.com/photo-1505373877841-8d25f7d46678?w=400'
};


// ============= PROVIDER CALENDAR PANEL =============
function ProviderCalendarPanel({ token }) {
  const [provider, setProvider] = useState(null);
  const [calendarData, setCalendarData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [capacityEdit, setCapacityEdit] = useState(3);
  const [savingCapacity, setSavingCapacity] = useState(false);
  const [connectingGcal, setConnectingGcal] = useState(false);
  const [managedServices, setManagedServices] = useState([]);
  const [showServices, setShowServices] = useState(true);
  const [bulkBlockMode, setBulkBlockMode] = useState(false);
  const [bulkStartDate, setBulkStartDate] = useState('');
  const [bulkEndDate, setBulkEndDate] = useState('');
  const [savingBulk, setSavingBulk] = useState(false);
  const [googleCalendars, setGoogleCalendars] = useState([]);
  const [loadingCalendars, setLoadingCalendars] = useState(false);
  const [selectedCalendarId, setSelectedCalendarId] = useState('primary');
  const headers = { Authorization: `Bearer ${token}` };

  const loadProvider = async () => {
    try {
      const res = await axios.get(`${API}/providers/me`, { headers });
      setProvider(res.data);
      setCapacityEdit(res.data.capacity_per_day || 3);
      setSelectedCalendarId(res.data.google_calendar_id || 'primary');
      const calRes = await axios.get(`${API}/providers/${res.data.provider_id}/calendar`, { headers });
      setCalendarData(calRes.data);
      // Load managed service names
      if (res.data.service_ids?.length > 0) {
        try {
          const svcRes = await axios.get(`${API}/services/products`, { headers });
          const allProducts = svcRes.data?.products || [];
          const managed = allProducts.filter(p => res.data.service_ids.includes(p.service_id));
          setManagedServices(managed);
        } catch { /* ignore */ }
      }
      // Load Google Calendar list if connected
      if (res.data.google_calendar_connected) {
        loadGoogleCalendars(res.data.provider_id);
      }
    } catch (err) {
      console.error('Provider load error:', err);
    } finally { setLoading(false); }
  };

  const loadGoogleCalendars = async (providerId) => {
    setLoadingCalendars(true);
    try {
      const res = await axios.get(`${API}/providers/${providerId}/calendars`, { headers });
      setGoogleCalendars(res.data.calendars || []);
      setSelectedCalendarId(res.data.selected_calendar_id || 'primary');
    } catch (err) {
      console.error('Failed to load Google calendars:', err);
    } finally { setLoadingCalendars(false); }
  };

  const selectCalendar = async (calendarId) => {
    try {
      await axios.put(`${API}/providers/${provider.provider_id}/calendar/select`, { calendar_id: calendarId }, { headers });
      setSelectedCalendarId(calendarId);
      const calName = googleCalendars.find(c => c.id === calendarId)?.summary || calendarId;
      toast.success(`Now syncing with "${calName}"`);
      loadProvider();
    } catch { toast.error('Failed to select calendar'); }
  };

  useEffect(() => { if (token) loadProvider(); }, [token]);

  const saveCapacity = async () => {
    setSavingCapacity(true);
    try {
      await axios.put(`${API}/providers/${provider.provider_id}`, { capacity_per_day: capacityEdit }, { headers });
      toast.success(`Capacity updated to ${capacityEdit}/day`);
      loadProvider();
    } catch { toast.error('Failed to update capacity'); }
    finally { setSavingCapacity(false); }
  };

  const toggleBlockDate = async (dateStr) => {
    const blocked = provider.blocked_dates || [];
    const newBlocked = blocked.includes(dateStr) ? blocked.filter(d => d !== dateStr) : [...blocked, dateStr];
    try {
      await axios.put(`${API}/providers/${provider.provider_id}/blocked-dates`, { blocked_dates: newBlocked }, { headers });
      toast.success(blocked.includes(dateStr) ? 'Date unblocked' : 'Date blocked');
      loadProvider();
    } catch { toast.error('Failed to update'); }
  };

  const connectGoogleCalendar = async () => {
    setConnectingGcal(true);
    try {
      const res = await axios.get(`${API}/oauth/calendar/connect/${provider.provider_id}`, { headers });
      if (res.data.authorization_url) {
        window.location.href = res.data.authorization_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start Google Calendar connection');
    } finally { setConnectingGcal(false); }
  };

  const disconnectGcal = async () => {
    try {
      await axios.post(`${API}/providers/${provider.provider_id}/calendar/disconnect`, {}, { headers });
      toast.success('Google Calendar disconnected');
      loadProvider();
    } catch { toast.error('Failed to disconnect'); }
  };

  const handleBulkBlock = async (action) => {
    if (!bulkStartDate || !bulkEndDate) {
      toast.error('Select both start and end dates');
      return;
    }
    if (bulkEndDate < bulkStartDate) {
      toast.error('End date must be after start date');
      return;
    }
    setSavingBulk(true);
    try {
      const blocked = new Set(provider.blocked_dates || []);
      const start = new Date(bulkStartDate + 'T12:00:00');
      const end = new Date(bulkEndDate + 'T12:00:00');
      const dates = [];
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        dates.push(d.toISOString().split('T')[0]);
      }
      if (action === 'block') {
        dates.forEach(d => blocked.add(d));
      } else {
        dates.forEach(d => blocked.delete(d));
      }
      await axios.put(`${API}/providers/${provider.provider_id}/blocked-dates`, { blocked_dates: Array.from(blocked) }, { headers });
      toast.success(`${dates.length} date${dates.length > 1 ? 's' : ''} ${action === 'block' ? 'blocked' : 'unblocked'}`);
      setBulkStartDate('');
      setBulkEndDate('');
      setBulkBlockMode(false);
      loadProvider();
    } catch { toast.error('Failed to update dates'); }
    finally { setSavingBulk(false); }
  };

  const clearAllBlocked = async () => {
    if (!window.confirm(`Clear all ${(provider.blocked_dates || []).length} blocked dates?`)) return;
    setSavingBulk(true);
    try {
      await axios.put(`${API}/providers/${provider.provider_id}/blocked-dates`, { blocked_dates: [] }, { headers });
      toast.success('All blocked dates cleared');
      loadProvider();
    } catch { toast.error('Failed to clear'); }
    finally { setSavingBulk(false); }
  };

  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-indigo-500" /></div>;
  }

  if (!provider) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="p-8 text-center">
          <CalendarIcon className="h-10 w-10 text-muted-foreground mx-auto mb-3 opacity-30" />
          <p className="font-medium text-foreground">No Provider Profile</p>
          <p className="text-sm text-muted-foreground">Contact admin to set up your provider profile</p>
        </CardContent>
      </Card>
    );
  }

  const days = calendarData?.days || {};
  const selectedDayData = selectedDate ? days[selectedDate] : null;

  // Get dates for calendar modifiers
  const blockedDates = (provider.blocked_dates || []).map(d => new Date(d + 'T12:00:00'));
  const bookedDates = Object.entries(days).filter(([, d]) => d.status === 'full').map(([k]) => new Date(k + 'T12:00:00'));
  const limitedDates = Object.entries(days).filter(([, d]) => d.status === 'limited').map(([k]) => new Date(k + 'T12:00:00'));

  return (
    <div className="space-y-6" data-testid="provider-calendar">
      {/* Provider Info & Settings */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-card border-border">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-foreground">{provider.capacity_per_day}</p>
            <p className="text-xs text-muted-foreground">Events Per Day</p>
            <div className="flex items-center gap-2 mt-2 justify-center">
              <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => setCapacityEdit(c => Math.max(1, c - 1))}>-</Button>
              <span className="text-sm font-bold w-6 text-center">{capacityEdit}</span>
              <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => setCapacityEdit(c => c + 1)}>+</Button>
              {capacityEdit !== provider.capacity_per_day && (
                <Button size="sm" onClick={saveCapacity} disabled={savingCapacity} className="h-7 text-xs bg-indigo-600">Save</Button>
              )}
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-indigo-400">{calendarData?.total_bookings || 0}</p>
            <p className="text-xs text-muted-foreground">Active Bookings</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-foreground">{provider.service_ids?.length || 0}</p>
            <p className="text-xs text-muted-foreground">Services Managed</p>
          </CardContent>
        </Card>
      </div>

      {/* Google Calendar Sync */}
      <Card className={`border-border ${provider.google_calendar_connected ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-indigo-500/5 border-indigo-500/20'}`} data-testid="gcal-sync-card">
        <CardContent className="p-4 space-y-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="p-2.5 rounded-xl bg-white/10">
              <svg className="h-6 w-6" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            </div>
            <div className="flex-1">
              {provider.google_calendar_connected ? (
                <>
                  <p className="font-semibold text-emerald-400">Google Calendar Connected</p>
                  <p className="text-xs text-muted-foreground">Synced with {provider.google_calendar_email}</p>
                </>
              ) : (
                <>
                  <p className="font-semibold text-foreground">Connect Google Calendar</p>
                  <p className="text-xs text-muted-foreground">Sync your Eventism bookings with Google Calendar and see your external events here.</p>
                </>
              )}
            </div>
            {provider.google_calendar_connected ? (
              <Button variant="outline" size="sm" onClick={disconnectGcal} data-testid="disconnect-gcal">Disconnect</Button>
            ) : (
              <Button className="bg-indigo-600 hover:bg-indigo-700" size="sm" onClick={connectGoogleCalendar} disabled={connectingGcal} data-testid="connect-gcal">
                {connectingGcal ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                Connect Google Calendar
              </Button>
            )}
          </div>

          {/* Calendar Selector — only shown when connected */}
          {provider.google_calendar_connected && (
            <div className="border-t border-border pt-3" data-testid="calendar-selector">
              <p className="text-xs font-semibold text-muted-foreground mb-2">Select which calendar to sync with Eventism:</p>
              {loadingCalendars ? (
                <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" /> Loading calendars...</div>
              ) : googleCalendars.length === 0 ? (
                <p className="text-xs text-muted-foreground">No calendars found. <button className="text-indigo-400 underline" onClick={() => loadGoogleCalendars(provider.provider_id)}>Retry</button></p>
              ) : (
                <div className="space-y-1.5 max-h-48 overflow-y-auto">
                  {googleCalendars.map(cal => (
                    <button
                      key={cal.id}
                      onClick={() => selectCalendar(cal.id)}
                      className={`w-full text-left flex items-center gap-3 p-2.5 rounded-lg border transition-all ${
                        selectedCalendarId === cal.id
                          ? 'border-emerald-500/50 bg-emerald-500/10'
                          : 'border-border bg-card hover:border-indigo-500/30 hover:bg-muted/30'
                      }`}
                      data-testid={`calendar-option-${cal.id}`}
                    >
                      <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: cal.background_color || '#4285f4' }} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{cal.summary}</p>
                        {cal.description && <p className="text-[10px] text-muted-foreground truncate">{cal.description}</p>}
                      </div>
                      {selectedCalendarId === cal.id && (
                        <Badge className="bg-emerald-500/20 text-emerald-400 text-[10px] flex-shrink-0">Active</Badge>
                      )}
                      {cal.primary && selectedCalendarId !== cal.id && (
                        <Badge variant="outline" className="text-[10px] flex-shrink-0">Primary</Badge>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Available Services */}
      <Card className="bg-card border-border" data-testid="managed-services-card">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading text-sm font-semibold text-foreground">Available Services ({managedServices.length})</h3>
            <Button size="sm" variant="ghost" className="text-xs" onClick={() => setShowServices(!showServices)} data-testid="toggle-services">
              {showServices ? 'Collapse' : 'Show All'}
            </Button>
          </div>
          {showServices && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[500px] overflow-y-auto pr-1" data-testid="services-list">
              {managedServices.map(svc => (
                <div key={svc.service_id} className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-muted/20 border border-border hover:border-indigo-500/30 transition-colors">
                  {svc.image_url && <img src={svc.image_url} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{svc.name}</p>
                    <p className="text-[10px] text-muted-foreground">{svc.category} — ${svc.base_price}/{svc.pricing_model?.replace('per_', '')}</p>
                  </div>
                  <Badge variant="outline" className="text-[10px] capitalize flex-shrink-0">{svc.category}</Badge>
                </div>
              ))}
            </div>
          )}
          {!showServices && managedServices.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {['food', 'drinks', 'entertainment', 'staffing', 'photography', 'decor'].map(cat => {
                const count = managedServices.filter(s => s.category === cat).length;
                if (!count) return null;
                return <Badge key={cat} variant="outline" className="text-[10px]">{cat}: {count}</Badge>;
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bulk Blackout Dates */}
      <Card className="bg-card border-border" data-testid="bulk-blackout-card">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading text-sm font-semibold text-foreground">
              Blackout Dates ({(provider.blocked_dates || []).length} blocked)
            </h3>
            <div className="flex gap-2">
              {(provider.blocked_dates || []).length > 0 && (
                <Button size="sm" variant="ghost" className="text-xs text-red-400 hover:text-red-300" onClick={clearAllBlocked} disabled={savingBulk} data-testid="clear-all-blocked">
                  Clear All
                </Button>
              )}
              <Button size="sm" variant={bulkBlockMode ? 'default' : 'outline'} className="text-xs" onClick={() => setBulkBlockMode(!bulkBlockMode)} data-testid="toggle-bulk-block">
                {bulkBlockMode ? 'Cancel' : 'Bulk Block/Unblock'}
              </Button>
            </div>
          </div>
          {bulkBlockMode && (
            <div className="space-y-3 p-3 bg-muted/30 rounded-lg border border-border" data-testid="bulk-block-form">
              <p className="text-xs text-muted-foreground">Select a date range to block or unblock multiple dates at once.</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Start Date</Label>
                  <Input type="date" value={bulkStartDate} onChange={e => setBulkStartDate(e.target.value)} className="h-8 text-sm" data-testid="bulk-start-date" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">End Date</Label>
                  <Input type="date" value={bulkEndDate} onChange={e => setBulkEndDate(e.target.value)} className="h-8 text-sm" data-testid="bulk-end-date" />
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" className="flex-1 bg-red-600 hover:bg-red-700 text-xs" onClick={() => handleBulkBlock('block')} disabled={savingBulk} data-testid="bulk-block-btn">
                  {savingBulk ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                  Block Date Range
                </Button>
                <Button size="sm" variant="outline" className="flex-1 text-xs" onClick={() => handleBulkBlock('unblock')} disabled={savingBulk} data-testid="bulk-unblock-btn">
                  Unblock Date Range
                </Button>
              </div>
            </div>
          )}
          {!bulkBlockMode && (provider.blocked_dates || []).length > 0 && (
            <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto">
              {(provider.blocked_dates || []).sort().slice(0, 20).map(d => (
                <Badge key={d} variant="outline" className="text-[10px] text-red-400 border-red-500/30">
                  {new Date(d + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </Badge>
              ))}
              {(provider.blocked_dates || []).length > 20 && (
                <Badge variant="outline" className="text-[10px]">+{(provider.blocked_dates || []).length - 20} more</Badge>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Calendar + Day Detail */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <Card className="lg:col-span-2 bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg">Booking Calendar</CardTitle>
            <div className="flex gap-4 text-xs text-muted-foreground mt-1">
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-500/30" /> Available</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-500/30" /> Limited</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-500/30" /> Full</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-slate-500/50" /> Blocked</span>
            </div>
          </CardHeader>
          <CardContent>
            <Calendar
              mode="single"
              selected={selectedDate ? new Date(selectedDate + 'T12:00:00') : undefined}
              onSelect={(date) => {
                if (date) setSelectedDate(date.toISOString().split('T')[0]);
              }}
              numberOfMonths={2}
              className="rounded-md border"
              modifiers={{
                blocked: blockedDates,
                full: bookedDates,
                limited: limitedDates,
              }}
              modifiersClassNames={{
                blocked: 'bg-slate-500/30 text-slate-400 line-through',
                full: 'bg-red-500/20 text-red-400',
                limited: 'bg-amber-500/20 text-amber-400',
              }}
              data-testid="provider-calendar-picker"
            />
          </CardContent>
        </Card>

        {/* Day Detail */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg">
              {selectedDate ? new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : 'Select a Date'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!selectedDate ? (
              <p className="text-sm text-muted-foreground text-center py-8">Click a date to see details</p>
            ) : selectedDayData ? (
              <div className="space-y-4">
                {/* Status */}
                <div className="flex items-center justify-between">
                  <Badge className={
                    selectedDayData.status === 'available' ? 'bg-emerald-500/20 text-emerald-400' :
                    selectedDayData.status === 'limited' ? 'bg-amber-500/20 text-amber-400' :
                    selectedDayData.status === 'full' ? 'bg-red-500/20 text-red-400' :
                    'bg-slate-500/20 text-slate-400'
                  }>{selectedDayData.status === 'available' ? 'Available' : selectedDayData.status === 'limited' ? 'Limited' : selectedDayData.status === 'full' ? 'Fully Booked' : 'Blocked'}</Badge>
                  <span className="text-xs text-muted-foreground">{selectedDayData.slots_available}/{selectedDayData.capacity} slots free</span>
                </div>

                {/* Block/Unblock */}
                <Button variant="outline" size="sm" className="w-full" onClick={() => toggleBlockDate(selectedDate)} data-testid="toggle-block-date">
                  {selectedDayData.blocked ? 'Unblock Date' : 'Block This Date'}
                </Button>

                {/* Eventism Bookings */}
                {selectedDayData.bookings?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground mb-2">Eventism Bookings</p>
                    {selectedDayData.bookings.map(b => (
                      <div key={b.booking_id} className="p-2.5 rounded-lg bg-indigo-500/10 border border-indigo-500/20 mb-2">
                        <p className="text-sm font-medium text-foreground">{b.client_name}</p>
                        <p className="text-[10px] text-muted-foreground">{b.event_type} • {b.event_time || 'TBD'} • {b.guest_count} guests</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Google Calendar Events */}
                {selectedDayData.google_events?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground mb-2">Google Calendar</p>
                    {selectedDayData.google_events.map((g, i) => (
                      <div key={i} className="p-2.5 rounded-lg bg-blue-500/10 border border-blue-500/20 mb-2">
                        <p className="text-sm font-medium text-foreground">{g.summary}</p>
                        <p className="text-[10px] text-muted-foreground">{g.all_day ? 'All day' : g.start_time?.split('T')[1]?.slice(0, 5)}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Empty */}
                {(!selectedDayData.bookings?.length && !selectedDayData.google_events?.length && !selectedDayData.blocked) && (
                  <p className="text-sm text-muted-foreground text-center py-4">No events on this date</p>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-8">No data for this date</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}


export default function VendorDashboard() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [vendorProfile, setVendorProfile] = useState(null);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [jobDetailDialog, setJobDetailDialog] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [selectedDates, setSelectedDates] = useState([]);
  const [savingAvailability, setSavingAvailability] = useState(false);
  const [jobFilter, setJobFilter] = useState('all');
  const fileInputRef = useRef(null);
  const portfolioInputRef = useRef(null);
  const deliverableInputRef = useRef(null);
  
  // Vendor agreements state
  const [agreements, setAgreements] = useState([]);
  const [respondingAgreement, setRespondingAgreement] = useState(null);
  
  // Stripe Connect state
  const [stripeStatus, setStripeStatus] = useState(null);
  const [connectingStripe, setConnectingStripe] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [showWithdrawDialog, setShowWithdrawDialog] = useState(false);
  
  const [profileForm, setProfileForm] = useState({
    business_name: '',
    description: '',
    category: 'chef',
    skills: '',
    hourly_rate: '',
    service_areas: ''
  });

  useEffect(() => {
    fetchData();
    
    // Check if returning from Google Calendar OAuth
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('calendar_connected')) {
      window.history.replaceState({}, '', '/vendor');
      toast.success('Google Calendar connected successfully!');
    }
    if (urlParams.get('calendar_error')) {
      window.history.replaceState({}, '', '/vendor');
      toast.error('Failed to connect Google Calendar. Please try again.');
    }

    // Check if returning from Stripe Connect onboarding
    if (urlParams.get('stripe_return') || urlParams.get('stripe_refresh')) {
      // Clean URL
      window.history.replaceState({}, '', '/dashboard');
      // Refresh stripe status
      fetchStripeStatus();
      if (urlParams.get('stripe_return')) {
        toast.success('Bank account connection updated!');
      }
    }
  }, []);

  const fetchStripeStatus = async () => {
    try {
      const res = await axios.get(`${API}/vendors/me/stripe-connect/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStripeStatus(res.data);
    } catch (error) {
      console.log('No Stripe status');
    }
  };

  const fetchData = async () => {
    try {
      try {
        const profileRes = await axios.get(`${API}/vendors/me`, { 
          headers: { Authorization: `Bearer ${token}` } 
        });
        setVendorProfile(profileRes.data);
        setProfileForm({
          business_name: profileRes.data.business_name,
          description: profileRes.data.description || '',
          category: profileRes.data.category,
          skills: profileRes.data.skills?.join(', ') || '',
          hourly_rate: profileRes.data.hourly_rate?.toString() || '',
          service_areas: profileRes.data.service_areas?.join(', ') || ''
        });
        
        // Parse availability dates
        if (profileRes.data.availability?.available_dates) {
          setSelectedDates(profileRes.data.availability.available_dates.map(d => new Date(d)));
        }
        
        // Fetch all data in parallel
        const [statsRes, jobsRes, invoicesRes, transactionsRes, shiftsRes, reviewsRes, agreementsRes] = await Promise.all([
          axios.get(`${API}/vendors/me/dashboard-stats`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null })),
          axios.get(`${API}/vendors/me/jobs`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { jobs: [] } })),
          axios.get(`${API}/vendors/me/invoices`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { invoices: [] } })),
          axios.get(`${API}/vendors/me/transactions`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { transactions: [] } })),
          axios.get(`${API}/shifts/my-shifts`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
          axios.get(`${API}/vendors/${profileRes.data.vendor_id}/reviews`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
          axios.get(`${API}/vendors/me/agreements`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
        ]);
        
        setDashboardStats(statsRes.data);
        setJobs(jobsRes.data.jobs || []);
        setInvoices(invoicesRes.data.invoices || []);
        setTransactions(transactionsRes.data.transactions || []);
        setShifts(shiftsRes.data || []);
        setReviews(reviewsRes.data || []);
        setAgreements(agreementsRes.data || []);
        
        // Fetch Stripe status
        fetchStripeStatus();
      } catch (e) {
        console.error('Error loading vendor data:', e);
        setVendorProfile(null);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Stripe Connect functions
  const handleRespondAgreement = async (agreementId, action) => {
    setRespondingAgreement(agreementId);
    try {
      await axios.post(`${API}/vendors/me/agreements/${agreementId}/respond`,
        { action },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(action === 'accept' ? 'Agreement accepted! You are now a preferred vendor.' : 'Agreement declined.');
      // Refresh agreements
      const res = await axios.get(`${API}/vendors/me/agreements`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }));
      setAgreements(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to respond');
    } finally {
      setRespondingAgreement(null);
    }
  };

  const handleConnectStripe = async () => {
    setConnectingStripe(true);
    try {
      const origin = window.location.origin;
      const res = await axios.post(`${API}/vendors/me/stripe-connect/onboard`, {}, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Origin': origin
        }
      });
      
      if (res.data.url) {
        // Redirect to Stripe Connect onboarding
        window.location.href = res.data.url;
      } else if (res.data.status === 'active') {
        toast.success('Bank account already connected!');
        fetchStripeStatus();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to connect Stripe');
    } finally {
      setConnectingStripe(false);
    }
  };

  const handleViewStripeDashboard = async () => {
    try {
      const res = await axios.get(`${API}/vendors/me/stripe-connect/dashboard-link`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.url) {
        window.open(res.data.url, '_blank');
      }
    } catch (error) {
      toast.error('Failed to open Stripe dashboard');
    }
  };

  const handleWithdraw = async () => {
    const amount = parseFloat(withdrawAmount);
    if (!amount || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    if (amount < 1) {
      toast.error('Minimum withdrawal is $1.00');
      return;
    }
    
    if (amount > (dashboardStats?.balance?.available || 0)) {
      toast.error('Insufficient balance');
      return;
    }

    setWithdrawing(true);
    try {
      const res = await axios.post(`${API}/vendors/me/stripe-connect/withdraw`, {
        amount: amount
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Successfully withdrew $${amount.toFixed(2)}!`);
      setShowWithdrawDialog(false);
      setWithdrawAmount('');
      fetchData(); // Refresh all data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Withdrawal failed');
    } finally {
      setWithdrawing(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!profileForm.business_name) {
      toast.error('Business name is required');
      return;
    }
    try {
      const payload = {
        business_name: profileForm.business_name,
        description: profileForm.description,
        category: profileForm.category,
        skills: profileForm.skills.split(',').map(s => s.trim()).filter(Boolean),
        hourly_rate: parseFloat(profileForm.hourly_rate) || 0,
        service_areas: profileForm.service_areas.split(',').map(s => s.trim()).filter(Boolean)
      };

      if (vendorProfile) {
        await axios.put(`${API}/vendors/me`, payload, { 
          headers: { Authorization: `Bearer ${token}` } 
        });
        toast.success('Profile updated');
      } else {
        await axios.post(`${API}/vendors/profile`, payload, { 
          headers: { Authorization: `Bearer ${token}` } 
        });
        toast.success('Profile created');
      }
      setProfileDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to save profile');
    }
  };

  const handleProfileImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploadingImage(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      await axios.patch(`${API}/vendors/me/image`, {
        image_path: response.data.path,
        image_type: 'profile'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Profile image uploaded!');
      fetchData();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handlePortfolioUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    setUploadingImage(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      await axios.patch(`${API}/vendors/me/image`, {
        image_path: response.data.path,
        image_type: 'portfolio'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Portfolio image added!');
      fetchData();
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
      if (portfolioInputRef.current) portfolioInputRef.current.value = '';
    }
  };

  const handleRemovePortfolioImage = async (index) => {
    try {
      await axios.delete(`${API}/vendors/me/portfolio/${index}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Image removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove image');
    }
  };

  const handleSaveAvailability = async () => {
    setSavingAvailability(true);
    try {
      await axios.patch(`${API}/vendors/me/availability`, {
        available_dates: selectedDates.map(d => d.toISOString().split('T')[0])
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Availability updated!');
    } catch (error) {
      toast.error('Failed to save availability');
    } finally {
      setSavingAvailability(false);
    }
  };

  const handleJobAction = async (assignmentId, action) => {
    try {
      await axios.put(`${API}/vendors/me/jobs/${assignmentId}/status`, {
        status: action
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Job ${action}!`);
      fetchData();
      setJobDetailDialog(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} job`);
    }
  };

  const handleUploadDeliverable = async (e, assignmentId) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingFile(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(`${API}/vendors/me/jobs/${assignmentId}/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('File uploaded successfully!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploadingFile(false);
      if (deliverableInputRef.current) deliverableInputRef.current.value = '';
    }
  };

  const getProfileImage = () => {
    if (vendorProfile?.profile_image) {
      return `${API}/files/${vendorProfile.profile_image}?auth=${token}`;
    }
    return PLACEHOLDER_IMAGES[vendorProfile?.category] || PLACEHOLDER_IMAGES.chef;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'TBD';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        weekday: 'short', month: 'short', day: 'numeric', year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleString('en-US', {
        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending': return <Badge variant="outline" className="text-amber-500 border-amber-500">Pending</Badge>;
      case 'notified': return <Badge className="bg-amber-500">Notified</Badge>;
      case 'accepted': return <Badge className="bg-blue-500">Accepted</Badge>;
      case 'declined': return <Badge variant="destructive">Declined</Badge>;
      case 'completed': return <Badge className="bg-green-500">Completed</Badge>;
      case 'paid': return <Badge className="bg-green-500">Paid</Badge>;
      case 'sent': return <Badge className="bg-amber-500">Awaiting Response</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  const filteredJobs = jobs.filter(job => {
    if (jobFilter === 'all') return true;
    return job.status === jobFilter;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-background flex items-center justify-center pt-16">
        <div className="animate-pulse text-muted-foreground">Loading dashboard...</div>
      </div>
    );
  }

  // Show onboarding if no vendor profile
  if (!vendorProfile) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-background pt-20 pb-12" data-testid="vendor-dashboard">
        <div className="max-w-2xl mx-auto px-4">
          <Card className="bg-card border-border">
            <CardContent className="py-12 text-center">
              <Briefcase className="h-16 w-16 text-indigo-500 mx-auto mb-6" />
              <h2 className="font-heading text-2xl font-bold text-foreground mb-2">
                Welcome to Eventism!
              </h2>
              <p className="text-muted-foreground mb-8">
                Create your vendor profile to start finding gigs and connecting with clients.
              </p>
              <Dialog open={profileDialogOpen} onOpenChange={setProfileDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-indigo-500 hover:bg-indigo-600" data-testid="create-profile-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Create Vendor Profile
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-card max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Create Your Profile</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4 max-h-[70vh] overflow-y-auto">
                    <div className="space-y-2">
                      <Label>Business Name *</Label>
                      <Input
                        value={profileForm.business_name}
                        onChange={(e) => setProfileForm({...profileForm, business_name: e.target.value})}
                        placeholder="Your business or professional name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Category *</Label>
                      <Select 
                        value={profileForm.category} 
                        onValueChange={(v) => setProfileForm({...profileForm, category: v})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CATEGORY_OPTIONS.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Hourly Rate ($)</Label>
                      <Input
                        type="number"
                        value={profileForm.hourly_rate}
                        onChange={(e) => setProfileForm({...profileForm, hourly_rate: e.target.value})}
                        placeholder="45"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Skills (comma-separated)</Label>
                      <Input
                        value={profileForm.skills}
                        onChange={(e) => setProfileForm({...profileForm, skills: e.target.value})}
                        placeholder="Food prep, Plating, Sauces"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Service Areas (comma-separated)</Label>
                      <Input
                        value={profileForm.service_areas}
                        onChange={(e) => setProfileForm({...profileForm, service_areas: e.target.value})}
                        placeholder="Los Angeles, Orange County"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Bio</Label>
                      <Textarea
                        value={profileForm.description}
                        onChange={(e) => setProfileForm({...profileForm, description: e.target.value})}
                        placeholder="Tell clients about your experience..."
                      />
                    </div>
                    <Button onClick={handleSaveProfile} className="w-full bg-indigo-500 hover:bg-indigo-600">
                      Create Profile
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-background pt-20 pb-12" data-testid="vendor-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Profile Header */}
        <Card className="bg-card border-border mb-8 overflow-hidden">
          <div className="relative h-32 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500" />
          <CardContent className="relative px-6 pb-6">
            <div className="flex flex-col sm:flex-row sm:items-end -mt-16 gap-4">
              {/* Profile Image */}
              <div className="relative group">
                <div className="w-32 h-32 rounded-xl border-4 border-background overflow-hidden bg-muted">
                  <img 
                    src={getProfileImage()}
                    alt={vendorProfile.business_name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleProfileImageUpload}
                  className="hidden"
                />
                <Button
                  size="icon"
                  variant="secondary"
                  className="absolute bottom-2 right-2 h-8 w-8 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingImage}
                  data-testid="upload-profile-image"
                >
                  <Camera className="h-4 w-4" />
                </Button>
              </div>

              {/* Profile Info */}
              <div className="flex-1">
                <div className="flex items-start justify-between">
                  <div>
                    <h1 className="font-heading text-2xl font-bold text-foreground">
                      {vendorProfile.business_name}
                    </h1>
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                      <Badge className="bg-indigo-500/20 text-indigo-500 border-indigo-500/30 capitalize">
                        {vendorProfile.category}
                      </Badge>
                      {vendorProfile.is_verified && (
                        <Badge className="bg-green-500/20 text-green-500 border-green-500/30">
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                          Verified
                        </Badge>
                      )}
                      <div className="flex items-center text-yellow-500">
                        <Star className="h-4 w-4 fill-yellow-500" />
                        <span className="ml-1 text-sm font-medium">{vendorProfile.rating?.toFixed(1) || 'N/A'}</span>
                        <span className="ml-1 text-xs text-muted-foreground">
                          ({vendorProfile.reviews_count || 0} reviews)
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 items-center">
                    <CreateEventButton />
                    <Button variant="outline" onClick={() => navigate('/staffing')} data-testid="find-shifts-btn">
                      Find Shifts
                    </Button>
                    <Dialog open={profileDialogOpen} onOpenChange={setProfileDialogOpen}>
                      <DialogTrigger asChild>
                        <Button variant="outline">Edit Profile</Button>
                      </DialogTrigger>
                      <DialogContent className="bg-card max-w-lg">
                        <DialogHeader>
                          <DialogTitle>Edit Profile</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 py-4 max-h-[70vh] overflow-y-auto">
                          <div className="space-y-2">
                            <Label>Business Name *</Label>
                            <Input
                              value={profileForm.business_name}
                              onChange={(e) => setProfileForm({...profileForm, business_name: e.target.value})}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Category *</Label>
                            <Select 
                              value={profileForm.category} 
                              onValueChange={(v) => setProfileForm({...profileForm, category: v})}
                            >
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {CATEGORY_OPTIONS.map(opt => (
                                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label>Hourly Rate ($)</Label>
                            <Input
                              type="number"
                              value={profileForm.hourly_rate}
                              onChange={(e) => setProfileForm({...profileForm, hourly_rate: e.target.value})}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Skills (comma-separated)</Label>
                            <Input
                              value={profileForm.skills}
                              onChange={(e) => setProfileForm({...profileForm, skills: e.target.value})}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Service Areas (comma-separated)</Label>
                            <Input
                              value={profileForm.service_areas}
                              onChange={(e) => setProfileForm({...profileForm, service_areas: e.target.value})}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Bio</Label>
                            <Textarea
                              value={profileForm.description}
                              onChange={(e) => setProfileForm({...profileForm, description: e.target.value})}
                            />
                          </div>
                          <Button onClick={handleSaveProfile} className="w-full bg-indigo-500 hover:bg-indigo-600">
                            Save Changes
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
                {vendorProfile.description && (
                  <p className="text-muted-foreground mt-2 text-sm">{vendorProfile.description}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="bg-card border-border">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Available Balance</p>
                  <p className="text-2xl font-bold text-green-500">
                    ${dashboardStats?.balance?.available?.toFixed(2) || '0.00'}
                  </p>
                </div>
                <div className="p-3 rounded-xl bg-green-500/10">
                  <Wallet className="h-6 w-6 text-green-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Pending Jobs</p>
                  <p className="text-2xl font-bold text-amber-500">{dashboardStats?.jobs?.pending || 0}</p>
                </div>
                <div className="p-3 rounded-xl bg-amber-500/10">
                  <Clock className="h-6 w-6 text-amber-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active Jobs</p>
                  <p className="text-2xl font-bold text-blue-500">{dashboardStats?.jobs?.accepted || 0}</p>
                </div>
                <div className="p-3 rounded-xl bg-blue-500/10">
                  <Briefcase className="h-6 w-6 text-blue-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Earned</p>
                  <p className="text-2xl font-bold text-foreground">
                    ${dashboardStats?.balance?.total_earned?.toFixed(2) || '0.00'}
                  </p>
                </div>
                <div className="p-3 rounded-xl bg-indigo-500/10">
                  <DollarSign className="h-6 w-6 text-indigo-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs Content */}
        <Tabs defaultValue="jobs" className="space-y-6">
          <TabsList className="bg-muted flex-wrap">
            <TabsTrigger value="jobs" data-testid="tab-jobs">
              <Briefcase className="h-4 w-4 mr-2" /> My Jobs ({jobs.length})
            </TabsTrigger>
            <TabsTrigger value="invoices" data-testid="tab-invoices">
              <Receipt className="h-4 w-4 mr-2" /> Invoices ({invoices.length})
            </TabsTrigger>
            <TabsTrigger value="earnings" data-testid="tab-earnings">
              <Wallet className="h-4 w-4 mr-2" /> Earnings
            </TabsTrigger>
            <TabsTrigger value="shifts" data-testid="tab-shifts">
              <CalendarIcon className="h-4 w-4 mr-2" /> Shifts
            </TabsTrigger>
            <TabsTrigger value="portfolio" data-testid="tab-portfolio">
              <ImageIcon className="h-4 w-4 mr-2" /> Portfolio
            </TabsTrigger>
            <TabsTrigger value="availability" data-testid="tab-availability">
              <CalendarIcon className="h-4 w-4 mr-2" /> Calendar
            </TabsTrigger>
            <TabsTrigger value="reviews" data-testid="tab-reviews">
              <Star className="h-4 w-4 mr-2" /> Reviews
            </TabsTrigger>
            <TabsTrigger value="agreements" data-testid="tab-agreements">
              <Handshake className="h-4 w-4 mr-2" /> Agreements
            </TabsTrigger>
          </TabsList>

          {/* My Jobs Tab */}
          <TabsContent value="jobs">
            <Card className="bg-card border-border">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="font-heading">Assigned Jobs</CardTitle>
                  <CardDescription>Jobs assigned to you by Eventism</CardDescription>
                </div>
                <Select value={jobFilter} onValueChange={setJobFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Filter" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Jobs</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="accepted">Accepted</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </CardHeader>
              <CardContent>
                {filteredJobs.length === 0 ? (
                  <div className="text-center py-12">
                    <Briefcase className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="font-heading text-lg font-semibold text-foreground mb-2">
                      No jobs found
                    </h3>
                    <p className="text-muted-foreground">
                      {jobFilter === 'all' ? "You haven't been assigned any jobs yet" : `No ${jobFilter} jobs`}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {filteredJobs.map(job => (
                      <Card key={job.assignment_id} className="bg-muted/30 border-border" data-testid={`job-${job.assignment_id}`}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <h4 className="font-semibold text-foreground">{job.event_title}</h4>
                                {getStatusBadge(job.status)}
                              </div>
                              <p className="text-sm text-muted-foreground mb-2">
                                <span className="font-medium">Service:</span> {job.service_name}
                              </p>
                              <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                                <div className="flex items-center">
                                  <CalendarIcon className="h-4 w-4 mr-1" />
                                  {formatDate(job.event_date)}
                                </div>
                                <div className="flex items-center">
                                  <MapPin className="h-4 w-4 mr-1" />
                                  {job.event_location || 'TBD'}
                                </div>
                                <div className="flex items-center">
                                  <Users className="h-4 w-4 mr-1" />
                                  {job.guest_count} guests
                                </div>
                              </div>
                              {job.invoice && (
                                <div className="mt-2 p-2 bg-green-500/10 rounded-lg inline-flex items-center gap-2">
                                  <DollarSign className="h-4 w-4 text-green-500" />
                                  <span className="text-sm font-medium text-green-500">
                                    ${job.invoice.amount?.toFixed(2)} - {job.invoice.status}
                                  </span>
                                </div>
                              )}
                            </div>
                            <div className="flex flex-col gap-2 ml-4">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setJobDetailDialog(job)}
                                data-testid={`view-job-${job.assignment_id}`}
                              >
                                <Eye className="h-4 w-4 mr-1" /> View
                              </Button>
                              {job.status === 'pending' || job.status === 'notified' ? (
                                <>
                                  <Button
                                    size="sm"
                                    className="bg-green-500 hover:bg-green-600"
                                    onClick={() => handleJobAction(job.assignment_id, 'accepted')}
                                    data-testid={`accept-job-${job.assignment_id}`}
                                  >
                                    <Check className="h-4 w-4 mr-1" /> Accept
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    onClick={() => handleJobAction(job.assignment_id, 'declined')}
                                  >
                                    <XCircle className="h-4 w-4 mr-1" /> Decline
                                  </Button>
                                </>
                              ) : job.status === 'accepted' ? (
                                <Button
                                  size="sm"
                                  className="bg-blue-500 hover:bg-blue-600"
                                  onClick={() => handleJobAction(job.assignment_id, 'completed')}
                                >
                                  <CheckCircle2 className="h-4 w-4 mr-1" /> Complete
                                </Button>
                              ) : null}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Invoices Tab */}
          <TabsContent value="invoices">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="font-heading">Purchase Orders</CardTitle>
                <CardDescription>Invoices and purchase orders from Eventism</CardDescription>
              </CardHeader>
              <CardContent>
                {invoices.length === 0 ? (
                  <div className="text-center py-12">
                    <Receipt className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="font-heading text-lg font-semibold text-foreground mb-2">
                      No invoices yet
                    </h3>
                    <p className="text-muted-foreground">
                      Purchase orders will appear here when Eventism assigns you to jobs
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {invoices.map(invoice => (
                      <Card key={invoice.invoice_id} className="bg-muted/30 border-border" data-testid={`invoice-${invoice.invoice_id}`}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="flex items-center gap-2 mb-2">
                                <h4 className="font-semibold text-foreground">{invoice.event_title}</h4>
                                {getStatusBadge(invoice.status)}
                              </div>
                              <p className="text-sm text-muted-foreground mb-1">
                                <span className="font-medium">Service:</span> {invoice.service_name}
                              </p>
                              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                <span><CalendarIcon className="h-4 w-4 inline mr-1" />{formatDate(invoice.event_date)}</span>
                                <span><MapPin className="h-4 w-4 inline mr-1" />{invoice.event_location || 'TBD'}</span>
                              </div>
                              {invoice.what_to_expect && (
                                <p className="text-sm text-muted-foreground mt-2">
                                  <span className="font-medium">Deliverables:</span> {invoice.what_to_expect.slice(0, 100)}...
                                </p>
                              )}
                            </div>
                            <div className="text-right">
                              <p className="text-2xl font-bold text-green-500">${invoice.amount?.toFixed(2)}</p>
                              <p className="text-xs text-muted-foreground">{invoice.currency || 'USD'}</p>
                              {invoice.status === 'paid' && invoice.paid_at && (
                                <p className="text-xs text-green-500 mt-1">
                                  Paid: {formatDateTime(invoice.paid_at)}
                                </p>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Earnings Tab */}
          <TabsContent value="earnings">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Balance Card */}
              <Card className="bg-card border-border lg:col-span-1">
                <CardHeader>
                  <CardTitle className="font-heading">Your Balance</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="p-4 bg-green-500/10 rounded-lg">
                      <p className="text-sm text-muted-foreground">Available for Withdrawal</p>
                      <p className="text-3xl font-bold text-green-500">
                        ${dashboardStats?.balance?.available?.toFixed(2) || '0.00'}
                      </p>
                    </div>
                    <Separator />
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Total Earned</span>
                        <span className="font-medium">${dashboardStats?.balance?.total_earned?.toFixed(2) || '0.00'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Pending</span>
                        <span className="font-medium text-amber-500">${dashboardStats?.balance?.pending?.toFixed(2) || '0.00'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Withdrawn</span>
                        <span className="font-medium">${dashboardStats?.balance?.withdrawn?.toFixed(2) || '0.00'}</span>
                      </div>
                    </div>
                    <Separator />
                    
                    {/* Stripe Connect Section */}
                    {stripeStatus?.connected && stripeStatus?.payouts_enabled ? (
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 text-green-500">
                          <CheckCircle2 className="h-4 w-4" />
                          <span className="text-sm font-medium">Bank Account Connected</span>
                        </div>
                        <Button 
                          className="w-full bg-green-600 hover:bg-green-700"
                          onClick={() => setShowWithdrawDialog(true)}
                          disabled={!dashboardStats?.balance?.available || dashboardStats?.balance?.available < 1}
                          data-testid="withdraw-btn"
                        >
                          <ArrowDownToLine className="h-4 w-4 mr-2" />
                          Withdraw Funds
                        </Button>
                        <Button 
                          variant="outline" 
                          className="w-full"
                          onClick={handleViewStripeDashboard}
                        >
                          <ExternalLink className="h-4 w-4 mr-2" />
                          Stripe Dashboard
                        </Button>
                      </div>
                    ) : stripeStatus?.connected && !stripeStatus?.payouts_enabled ? (
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 text-amber-500">
                          <Clock className="h-4 w-4" />
                          <span className="text-sm font-medium">Setup Incomplete</span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Please complete your Stripe account setup to enable withdrawals.
                        </p>
                        <Button 
                          className="w-full"
                          onClick={handleConnectStripe}
                          disabled={connectingStripe}
                        >
                          {connectingStripe ? (
                            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Connecting...</>
                          ) : (
                            <>Complete Setup</>
                          )}
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="p-3 bg-muted/50 rounded-lg">
                          <h4 className="font-medium text-sm mb-1">Connect Your Bank Account</h4>
                          <p className="text-xs text-muted-foreground">
                            Link your bank account via Stripe to withdraw your earnings securely.
                          </p>
                        </div>
                        <Button 
                          className="w-full bg-indigo-500 hover:bg-indigo-600"
                          onClick={handleConnectStripe}
                          disabled={connectingStripe}
                          data-testid="connect-stripe-btn"
                        >
                          {connectingStripe ? (
                            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Connecting...</>
                          ) : (
                            <><Building className="h-4 w-4 mr-2" /> Connect Bank Account</>
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Transaction History */}
              <Card className="bg-card border-border lg:col-span-2">
                <CardHeader>
                  <CardTitle className="font-heading">Transaction History</CardTitle>
                </CardHeader>
                <CardContent>
                  {transactions.length === 0 ? (
                    <div className="text-center py-8">
                      <DollarSign className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                      <p className="text-muted-foreground">No transactions yet</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {transactions.map(tx => (
                        <div key={tx.transaction_id} className="flex items-center justify-between p-3 border rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-full ${tx.transaction_type === 'credit' ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                              {tx.transaction_type === 'credit' ? (
                                <Plus className="h-4 w-4 text-green-500" />
                              ) : (
                                <ArrowDownToLine className="h-4 w-4 text-red-500" />
                              )}
                            </div>
                            <div>
                              <p className="font-medium text-sm">{tx.description}</p>
                              <p className="text-xs text-muted-foreground">{formatDateTime(tx.created_at)}</p>
                            </div>
                          </div>
                          <p className={`font-bold ${tx.transaction_type === 'credit' ? 'text-green-500' : 'text-red-500'}`}>
                            {tx.transaction_type === 'credit' ? '+' : '-'}${tx.amount?.toFixed(2)}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Shifts Tab */}
          <TabsContent value="shifts">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="font-heading">Your Claimed Shifts</CardTitle>
              </CardHeader>
              <CardContent>
                {shifts.length === 0 ? (
                  <div className="text-center py-12">
                    <CalendarIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="font-heading text-lg font-semibold text-foreground mb-2">
                      No claimed shifts
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      Browse the job board to find and claim available shifts
                    </p>
                    <Button onClick={() => navigate('/staffing')}>
                      Browse Staffing Jobs
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {shifts.map(shift => (
                      <Card key={shift.shift_id} className="bg-muted/30 border-border">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div>
                              <h4 className="font-semibold text-foreground">{shift.title}</h4>
                              <p className="text-sm text-muted-foreground">{shift.description}</p>
                              <div className="flex items-center space-x-4 mt-2 text-sm text-muted-foreground">
                                <div className="flex items-center">
                                  <CalendarIcon className="h-4 w-4 mr-1" />
                                  {new Date(shift.date).toLocaleDateString()}
                                </div>
                                <div className="flex items-center">
                                  <Clock className="h-4 w-4 mr-1" />
                                  {shift.start_time} - {shift.end_time}
                                </div>
                                <div className="flex items-center">
                                  <MapPin className="h-4 w-4 mr-1" />
                                  {shift.location}
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-lg font-bold text-green-500">${shift.hourly_rate}/hr</p>
                              <Badge className="capitalize">{shift.status}</Badge>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Portfolio Tab */}
          <TabsContent value="portfolio">
            <Card className="bg-card border-border">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-heading">Portfolio Gallery</CardTitle>
                <div>
                  <input
                    ref={portfolioInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handlePortfolioUpload}
                    className="hidden"
                  />
                  <Button 
                    onClick={() => portfolioInputRef.current?.click()}
                    disabled={uploadingImage}
                    data-testid="add-portfolio-image"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {uploadingImage ? 'Uploading...' : 'Add Image'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {(!vendorProfile.portfolio_images || vendorProfile.portfolio_images.length === 0) ? (
                  <div className="text-center py-12">
                    <ImageIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="font-heading text-lg font-semibold text-foreground mb-2">
                      No portfolio images yet
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      Upload photos of your work to showcase your skills
                    </p>
                    <Button onClick={() => portfolioInputRef.current?.click()}>
                      <Camera className="h-4 w-4 mr-2" />
                      Upload First Image
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {vendorProfile.portfolio_images.map((img, idx) => (
                      <div key={idx} className="relative group aspect-square rounded-lg overflow-hidden bg-muted">
                        <img 
                          src={`${API}/files/${img}?auth=${token}`}
                          alt={`Portfolio ${idx + 1}`}
                          className="w-full h-full object-cover"
                        />
                        <Button
                          size="icon"
                          variant="destructive"
                          className="absolute top-2 right-2 h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => handleRemovePortfolioImage(idx)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Availability Tab */}
          <TabsContent value="availability" data-testid="calendar-tab">
            <ProviderCalendarPanel token={token} />
          </TabsContent>

          {/* Reviews Tab */}
          <TabsContent value="reviews">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="font-heading">Client Reviews</CardTitle>
              </CardHeader>
              <CardContent>
                {reviews.length === 0 ? (
                  <div className="text-center py-12">
                    <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="font-heading text-lg font-semibold text-foreground mb-2">
                      No reviews yet
                    </h3>
                    <p className="text-muted-foreground">
                      Complete jobs to start receiving reviews from clients
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {reviews.map((review, idx) => (
                      <Card key={idx} className="bg-muted/30 border-border">
                        <CardContent className="p-4">
                          <div className="flex items-start gap-4">
                            <div className="w-10 h-10 rounded-full bg-indigo-500/20 flex items-center justify-center">
                              <User className="h-5 w-5 text-indigo-500" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-1">
                                <span className="font-medium text-foreground">{review.reviewer_name}</span>
                                <div className="flex items-center text-yellow-500">
                                  {[...Array(5)].map((_, i) => (
                                    <Star 
                                      key={i} 
                                      className={`h-4 w-4 ${i < review.rating ? 'fill-yellow-500' : 'fill-muted'}`} 
                                    />
                                  ))}
                                </div>
                              </div>
                              <p className="text-sm text-muted-foreground">{review.comment}</p>
                              <p className="text-xs text-muted-foreground mt-2">
                                {new Date(review.created_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Agreements Tab */}
          <TabsContent value="agreements">
            <Card>
              <CardHeader>
                <CardTitle>Preferred Vendor Agreements</CardTitle>
                <CardDescription>
                  Manage your preferred vendor agreements with Eventism services
                </CardDescription>
              </CardHeader>
              <CardContent>
                {agreements.length === 0 ? (
                  <div className="text-center py-8">
                    <Handshake className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                    <p className="text-muted-foreground">No agreements yet</p>
                    <p className="text-sm text-muted-foreground">When Eventism adds you as a preferred vendor for a service, it will appear here.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {agreements.map((agree) => (
                      <div
                        key={agree.agreement_id}
                        className="border rounded-lg p-4"
                        data-testid={`agreement-${agree.agreement_id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-semibold">{agree.service_name}</h4>
                            <p className="text-sm text-muted-foreground">
                              {agree.service_category} &middot; Requested {new Date(agree.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            {agree.status === 'pending' ? (
                              <>
                                <Button
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700"
                                  onClick={() => handleRespondAgreement(agree.agreement_id, 'accept')}
                                  disabled={respondingAgreement === agree.agreement_id}
                                  data-testid={`accept-${agree.agreement_id}`}
                                >
                                  Accept
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="border-red-300 text-red-600"
                                  onClick={() => handleRespondAgreement(agree.agreement_id, 'decline')}
                                  disabled={respondingAgreement === agree.agreement_id}
                                  data-testid={`decline-${agree.agreement_id}`}
                                >
                                  Decline
                                </Button>
                              </>
                            ) : (
                              <Badge variant={agree.status === 'active' ? 'default' : 'destructive'}>
                                {agree.status}
                              </Badge>
                            )}
                          </div>
                        </div>
                        {agree.status === 'active' && (
                          <p className="text-xs text-muted-foreground mt-2 bg-muted/50 rounded p-2">
                            You've agreed to allow Eventism to use your wholesale services as a preferred vendor for this offering.
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Job Detail Dialog */}
      <Dialog open={!!jobDetailDialog} onOpenChange={() => setJobDetailDialog(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {jobDetailDialog && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Briefcase className="h-5 w-5 text-indigo-500" />
                  {jobDetailDialog.event_title}
                </DialogTitle>
                <DialogDescription>
                  Job details and actions
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-4">
                {/* Status */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Status:</span>
                  {getStatusBadge(jobDetailDialog.status)}
                </div>

                {/* Event Details */}
                <div className="grid grid-cols-2 gap-4 p-4 bg-muted/30 rounded-lg">
                  <div>
                    <label className="text-xs text-muted-foreground">Service</label>
                    <p className="font-medium">{jobDetailDialog.service_name}</p>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Event Date</label>
                    <p className="font-medium">{formatDate(jobDetailDialog.event_date)}</p>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Location</label>
                    <p className="font-medium">{jobDetailDialog.event_location || 'TBD'}</p>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Guests</label>
                    <p className="font-medium">{jobDetailDialog.guest_count}</p>
                  </div>
                </div>

                {/* Invoice Details */}
                {jobDetailDialog.invoice && (
                  <div className="p-4 border rounded-lg">
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      <Receipt className="h-4 w-4" /> Purchase Order
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs text-muted-foreground">Amount</label>
                        <p className="text-xl font-bold text-green-500">
                          ${jobDetailDialog.invoice.amount?.toFixed(2)}
                        </p>
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground">Status</label>
                        <p>{getStatusBadge(jobDetailDialog.invoice.status)}</p>
                      </div>
                    </div>
                    {jobDetailDialog.invoice.what_to_expect && (
                      <div className="mt-3">
                        <label className="text-xs text-muted-foreground">What to Expect (Deliverables)</label>
                        <p className="text-sm">{jobDetailDialog.invoice.what_to_expect}</p>
                      </div>
                    )}
                    {jobDetailDialog.invoice.what_we_provide && (
                      <div className="mt-3">
                        <label className="text-xs text-muted-foreground">What Eventism Provides</label>
                        <p className="text-sm">{jobDetailDialog.invoice.what_we_provide}</p>
                      </div>
                    )}
                    {jobDetailDialog.invoice.special_requirements && (
                      <div className="mt-3">
                        <label className="text-xs text-muted-foreground">Special Requirements</label>
                        <p className="text-sm">{jobDetailDialog.invoice.special_requirements}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* File Upload Section (only for accepted jobs) */}
                {(jobDetailDialog.status === 'accepted' || jobDetailDialog.status === 'completed') && (
                  <div className="p-4 border rounded-lg">
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      <Upload className="h-4 w-4" /> Upload Deliverables
                    </h4>
                    <p className="text-sm text-muted-foreground mb-3">
                      Upload files related to this job (photos, documents, etc.)
                    </p>
                    <input
                      ref={deliverableInputRef}
                      type="file"
                      onChange={(e) => handleUploadDeliverable(e, jobDetailDialog.assignment_id)}
                      className="hidden"
                    />
                    <Button 
                      onClick={() => deliverableInputRef.current?.click()}
                      disabled={uploadingFile}
                    >
                      {uploadingFile ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...</>
                      ) : (
                        <><Upload className="h-4 w-4 mr-2" /> Upload File</>
                      )}
                    </Button>
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setJobDetailDialog(null)}>
                  Close
                </Button>
                {(jobDetailDialog.status === 'pending' || jobDetailDialog.status === 'notified') && (
                  <>
                    <Button
                      variant="destructive"
                      onClick={() => handleJobAction(jobDetailDialog.assignment_id, 'declined')}
                    >
                      <XCircle className="h-4 w-4 mr-2" /> Decline
                    </Button>
                    <Button
                      className="bg-green-500 hover:bg-green-600"
                      onClick={() => handleJobAction(jobDetailDialog.assignment_id, 'accepted')}
                    >
                      <Check className="h-4 w-4 mr-2" /> Accept Job
                    </Button>
                  </>
                )}
                {jobDetailDialog.status === 'accepted' && (
                  <Button
                    className="bg-blue-500 hover:bg-blue-600"
                    onClick={() => handleJobAction(jobDetailDialog.assignment_id, 'completed')}
                  >
                    <CheckCircle2 className="h-4 w-4 mr-2" /> Mark Complete
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Withdrawal Dialog */}
      <Dialog open={showWithdrawDialog} onOpenChange={setShowWithdrawDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowDownToLine className="h-5 w-5 text-green-500" />
              Withdraw Funds
            </DialogTitle>
            <DialogDescription>
              Transfer funds to your connected bank account via Stripe
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="p-4 bg-green-500/10 rounded-lg">
              <p className="text-sm text-muted-foreground">Available Balance</p>
              <p className="text-2xl font-bold text-green-500">
                ${dashboardStats?.balance?.available?.toFixed(2) || '0.00'}
              </p>
            </div>

            <div>
              <Label>Withdrawal Amount</Label>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-lg font-bold">$</span>
                <Input
                  type="number"
                  step="0.01"
                  min="1"
                  max={dashboardStats?.balance?.available || 0}
                  value={withdrawAmount}
                  onChange={(e) => setWithdrawAmount(e.target.value)}
                  placeholder="0.00"
                  className="flex-1"
                  data-testid="withdraw-amount-input"
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Minimum withdrawal: $1.00
              </p>
            </div>

            <div className="p-3 bg-muted/50 rounded-lg text-sm">
              <p className="font-medium mb-1">Transfer Details</p>
              <p className="text-muted-foreground text-xs">
                Funds will be transferred to your connected Stripe account and then to your bank. 
                This typically takes 2-3 business days.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowWithdrawDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleWithdraw} 
              disabled={withdrawing || !withdrawAmount || parseFloat(withdrawAmount) < 1}
              className="bg-green-600 hover:bg-green-700"
              data-testid="confirm-withdraw-btn"
            >
              {withdrawing ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Processing...</>
              ) : (
                <><ArrowDownToLine className="h-4 w-4 mr-2" /> Withdraw ${withdrawAmount || '0.00'}</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
